import React, { useState, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import { Typography, Tooltip, Breadcrumbs } from '@mui/material';
import MUIDataTable from 'mui-datatables';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import moment from 'moment';
import Page from '../../../components/Page';
import PageHeader from '../../../components/PageHeader';
import Reports from '../../../services/report/reports';

const STATUS_COLORS = {
  delivered: 'green',
  sent: 'gray',
  bounced: 'red',
  complained: 'red',
  failed: 'red',
};

export default function EmailTracking() {
  const classes = useStyles();
  const navigate = useNavigate();
  const [emailList, setEmailList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchKey, setSearchKey] = useState('');
  const [sortKey, setSortKey] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const getEmailTrackingList = async (pageNum, rows, sortOrd, sortCol, searchWord) => {
    setEmailList([]);
    setLoading(true);
    try {
      const response = await Reports.getEmailTrackingList(pageNum, rows, sortOrd, sortCol, searchWord);
      if (response.ok) {
        setEmailList(response.data.records);
        setPageCount(response?.data?.metadata?.totalRecordCount);
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    getEmailTrackingList(page, rowsPerPage, sortOrder, sortKey, searchKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      name: 'id',
      label: 'Id',
      options: { filter: false, sort: false, display: false },
    },
    {
      name: 'recipient_email',
      label: 'Recipient',
      options: {
        filter: true,
        sort: false,
        customBodyRender: (value) => <Typography className={classes.name}>{value}</Typography>,
      },
    },
    {
      name: 'email_type',
      label: 'Type',
      options: {
        filter: true,
        sort: false,
        customBodyRender: (value) => <Typography className={classes.name}>{value}</Typography>,
      },
    },
    {
      name: 'subject',
      label: 'Subject',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) =>
          value && value.length >= 30 ? (
            <Tooltip title={value}>
              <Typography className={classes.name}>{value}</Typography>
            </Tooltip>
          ) : (
            <Typography className={classes.name}>{value}</Typography>
          ),
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        filter: true,
        sort: false,
        customBodyRender: (value) => (
          <Typography
            sx={{
              fontWeight: 400,
              backgroundColor: STATUS_COLORS[value] || 'gray',
              textAlign: 'center',
              borderRadius: '24px',
              minWidth: '91px',
              padding: '3px 0px',
              fontSize: '0.9rem',
              color: 'white',
              textTransform: 'capitalize',
            }}
          >
            {value}
          </Typography>
        ),
      },
    },
    {
      name: 'click_count',
      label: 'Link Clicks',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => <Typography className={classes.name}>{value}</Typography>,
      },
    },
    {
      name: 'last_clicked_at',
      label: 'Last Clicked',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => (
          <Typography className={classes.name}>{value ? moment(value).format('MM-DD-YYYY HH:mm') : '-'}</Typography>
        ),
      },
    },
    {
      name: 'created_at',
      label: 'Sent At',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => <Typography className={classes.name}>{moment(value).format('MM-DD-YYYY HH:mm')}</Typography>,
      },
    },
  ];

  const Breadcrumb = () => (
    <Breadcrumbs aria-label="breadcrumb" separator="›">
      <Typography sx={{ textDecoration: 'none' }} variant="body2" color="primary" component={RouterLink} to="/app/dashboard">
        Dashboard
      </Typography>
      <Typography variant="body2" color="textPrimary">
        Email Tracking
      </Typography>
    </Breadcrumbs>
  );

  return (
    <Page title="Email Tracking">
      {/* Page's Container is padding:0 and the dashboard Outlet adds none either,
          so gutters come from the page - same wrapper the Plans & Pricing page uses. */}
      <div className="px-4 md:px-6 pt-4 pb-12 min-h-screen bg-gray-50">
        <PageHeader
          pageTitle="Email Tracking"
          subTitle="Delivery and click activity for every email the platform sends."
          breadcrumbs={<Breadcrumb />}
          onBack={() => navigate('/app/dashboard')}
        />
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden [&_.MuiPaper-root]:shadow-none [&_.MuiPaper-root]:rounded-none [&_.MuiPaper-root]:bg-transparent">
          <MUIDataTable
            data={emailList}
            columns={columns}
            options={{
              pagination: true,
              filter: false,
              responsive: 'scroll',
              print: false,
              jumpToPage: true,
              download: true,
              page,
              search: true,
              count: pageCount,
              serverSide: true,
              rowsPerPage,
              rowsPerPageOptions: [10, 25, 50, 75, 100],
              selectableRows: false,
              textLabels: {
                body: { noMatch: loading ? 'loading' : 'sorry, no matching records found' },
                pagination: { jumpToPage: 'jumpToPage', rowsPerPage: 'rowsPerPage' },
              },
              onTableChange: (action, tableState) => {
                if (!['changeRowsPerPage', 'changePage', 'search', 'sort'].includes(action)) {
                  return;
                }
                const nextPage = action === 'sort' ? tableState.page : tableState.page + 1;
                const nextSortOrder = tableState.sortOrder.direction !== undefined ? tableState.sortOrder.direction : '';
                const nextSortKey = tableState.sortOrder.name !== undefined ? tableState.sortOrder.name : '';
                const nextSearchKey = tableState.searchText !== null ? tableState.searchText : '';

                setRowsPerPage(tableState.rowsPerPage);
                setEmailList([]);
                setPage(tableState.page);
                setSortKey(nextSortKey);
                setSearchKey(nextSearchKey);
                setSortOrder(nextSortOrder);
                getEmailTrackingList(nextPage, tableState.rowsPerPage, nextSortOrder, nextSortKey, nextSearchKey);
              },
            }}
              />
        </div>
      </div>
    </Page>
  );
}

const useStyles = makeStyles((theme) => ({
  name: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    maxWidth: '200px',
    [theme.breakpoints.down('md')]: {
      maxWidth: 'unset',
    },
  },
}));
